import { supabase, adminSupabase } from './supabaseService';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface ChatMessage {
    id: string;
    user_id: string;
    wallet_address: string;
    message: string;
    user_name: string;
    avatar_url: string;
    user_color: string;
    timestamp: string;
    message_type: 'text' | 'system' | 'image';
    reply_to?: string;
    is_deleted: boolean;
    sequence_id?: number;
}

export interface ChatPreferences {
    id?: string;
    user_id: string;
    display_wallet_address: boolean; // Legacy field - will be deprecated
    display_own_wallet_address: boolean; // Controls displaying user's own wallet address
    display_others_wallet_address: boolean; // Controls displaying other users' wallet addresses
    notification_sound: boolean;
    email_notifications: boolean;
    user_color: string;
    user_status: 'online' | 'away' | 'busy';
}

class ChatService {
    private chatChannel: RealtimeChannel | null = null;
    private listeners: ((message: ChatMessage) => void)[] = [];

    /**
     * Initialize chat service
     */
    async init() {
        // Set up Supabase Realtime channel subscription
        // This will be called when the user connects to the community page
        this.closeSubscription(); // Close any existing subscription

        // Create a new subscription
        this.chatChannel = supabase
            .channel('chat-messages')
            .on('postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'chat_messages'
                },
                (payload) => {
                    console.log('New chat message received:', payload);
                    const message = payload.new as ChatMessage;
                    // Notify all listeners of new message
                    this.listeners.forEach(listener => listener(message));
                }
            )
            .subscribe();

        console.log('Chat service initialized');
    }

    /**
     * Close subscription to prevent memory leaks
     */
    closeSubscription() {
        if (this.chatChannel) {
            supabase.removeChannel(this.chatChannel);
            this.chatChannel = null;
            this.listeners = [];
            console.log('Chat subscription closed');
        }
    }

    /**
     * Subscribe to new chat messages
     */
    subscribe(callback: (message: ChatMessage) => void) {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(listener => listener !== callback);
        };
    }

    /**
     * Fetch chat history
     * @param limit Number of messages to fetch
     * @param before Fetch messages before this sequence ID
     */
    async getChatHistory(limit: number = 50, before?: string, beforeSequence?: number): Promise<ChatMessage[]> {
        try {
            const query = adminSupabase
                .from('chat_messages')
                .select('*');

            // If we have a sequence ID, use it for pagination as it's more reliable
            if (beforeSequence) {
                query.lt('sequence_id', beforeSequence)
                    .order('sequence_id', { ascending: false })
                    .limit(limit);
            }
            // Otherwise fall back to timestamp-based pagination
            else if (before) {
                query.lt('timestamp', before)
                    .order('timestamp', { ascending: false })
                    .limit(limit);
            }
            // Initial load - order by sequence_id which is more reliable than timestamp
            else {
                query.order('sequence_id', { ascending: false })
                    .limit(limit);
            }

            const { data, error } = await query;

            if (error) {
                console.error('Error fetching chat history:', error);
                return [];
            }

            // Return in chronological order (oldest first)
            return (data || []).reverse() as ChatMessage[];
        } catch (error) {
            console.error('Exception fetching chat history:', error);
            return [];
        }
    }

    /**
     * Send a chat message
     */
    async sendMessage(
        user_id: string,
        wallet_address: string,
        message: string,
        user_name: string,
        avatar_url: string,
        user_color: string
    ): Promise<ChatMessage | null> {
        try {
            const { data, error } = await adminSupabase
                .from('chat_messages')
                .insert({
                    user_id,
                    wallet_address,
                    message,
                    user_name,
                    avatar_url,
                    user_color,
                    timestamp: new Date().toISOString()
                })
                .select()
                .single();

            if (error) {
                console.error('Error sending chat message:', error);
                return null;
            }

            return data as ChatMessage;
        } catch (error) {
            console.error('Exception sending chat message:', error);
            return null;
        }
    }

    /**
     * Send a system message
     */
    async sendSystemMessage(message: string): Promise<ChatMessage | null> {
        try {
            const { data, error } = await adminSupabase
                .from('chat_messages')
                .insert({
                    user_id: '00000000-0000-0000-0000-000000000000', // System user ID
                    wallet_address: 'system',
                    message,
                    user_name: 'System',
                    message_type: 'system',
                    timestamp: new Date().toISOString()
                })
                .select()
                .single();

            if (error) {
                console.error('Error sending system message:', error);
                return null;
            }

            return data as ChatMessage;
        } catch (error) {
            console.error('Exception sending system message:', error);
            return null;
        }
    }

    /**
     * Get user chat preferences
     */
    async getChatPreferences(user_id: string): Promise<ChatPreferences | null> {
        try {
            // First check if preferences exist
            const { data, error } = await adminSupabase
                .from('chat_preferences')
                .select('*')
                .eq('user_id', user_id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') { // Not found
                    // Create default preferences
                    return this.saveChatPreferences({
                        user_id,
                        display_wallet_address: true,
                        display_own_wallet_address: true,
                        display_others_wallet_address: true,
                        notification_sound: true,
                        email_notifications: false,
                        user_color: '#42ae84',
                        user_status: 'online'
                    });
                }

                console.error('Error fetching chat preferences:', error);
                return null;
            }

            return data as ChatPreferences;
        } catch (error) {
            console.error('Exception fetching chat preferences:', error);
            return null;
        }
    }

    /**
     * Save user chat preferences
     */
    async saveChatPreferences(preferences: ChatPreferences): Promise<ChatPreferences | null> {
        try {
            // Check if preferences exist
            const { data: existing } = await adminSupabase
                .from('chat_preferences')
                .select('id')
                .eq('user_id', preferences.user_id)
                .single();

            let result;

            if (existing) {
                // Update existing preferences
                const { data, error } = await adminSupabase
                    .from('chat_preferences')
                    .update({
                        display_wallet_address: preferences.display_wallet_address,
                        display_own_wallet_address: preferences.display_own_wallet_address,
                        display_others_wallet_address: preferences.display_others_wallet_address,
                        notification_sound: preferences.notification_sound,
                        email_notifications: preferences.email_notifications,
                        user_color: preferences.user_color,
                        user_status: preferences.user_status,
                        updated_at: new Date().toISOString()
                    })
                    .eq('user_id', preferences.user_id)
                    .select()
                    .single();

                if (error) {
                    console.error('Error updating chat preferences:', error);
                    return null;
                }

                result = data;
            } else {
                // Insert new preferences
                const { data, error } = await adminSupabase
                    .from('chat_preferences')
                    .insert({
                        user_id: preferences.user_id,
                        display_wallet_address: preferences.display_wallet_address,
                        display_own_wallet_address: preferences.display_own_wallet_address,
                        display_others_wallet_address: preferences.display_others_wallet_address,
                        notification_sound: preferences.notification_sound,
                        email_notifications: preferences.email_notifications,
                        user_color: preferences.user_color,
                        user_status: preferences.user_status
                    })
                    .select()
                    .single();

                if (error) {
                    console.error('Error inserting chat preferences:', error);
                    return null;
                }

                result = data;
            }

            return result as ChatPreferences;
        } catch (error) {
            console.error('Exception saving chat preferences:', error);
            return null;
        }
    }

    /**
     * Update user status
     */
    async updateUserStatus(user_id: string, status: 'online' | 'away' | 'busy'): Promise<boolean> {
        try {
            const { error } = await adminSupabase
                .from('chat_preferences')
                .update({
                    user_status: status,
                    updated_at: new Date().toISOString()
                })
                .eq('user_id', user_id);

            if (error) {
                console.error('Error updating user status:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Exception updating user status:', error);
            return false;
        }
    }
}

// Export a singleton instance
export const chatService = new ChatService();

export default chatService; 